<template>
  <div class="pdf-reminder-page">
    <!-- 查询区 -->
    <el-card class="search-card" shadow="never">
      <el-form :model="queryForm" inline size="small" class="search-form">
        <el-form-item label="设备名称">
          <el-input v-model="queryForm.device_name" placeholder="设备名称" clearable style="width: 180px" />
        </el-form-item>
        <el-form-item label="科室">
          <department-select v-model="queryForm.department" placeholder="全部科室" clearable style="width: 160px" />
        </el-form-item>
        <el-form-item label="报告日期">
          <el-date-picker v-model="queryForm.report_date_start" type="date" value-format="yyyy-MM-dd" placeholder="最早" style="width: 150px" />
          <span class="range-separator">-</span>
          <el-date-picker v-model="queryForm.report_date_end" type="date" value-format="yyyy-MM-dd" placeholder="最晚" style="width: 150px" />
        </el-form-item>
        <el-form-item>
          <el-button type="primary" icon="el-icon-search" @click="handleSearch">查询</el-button>
          <el-button icon="el-icon-refresh-right" @click="handleReset">重置</el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 操作按钮 -->
    <div class="toolbar">
      <el-button type="primary" icon="el-icon-plus" @click="handleAdd">新增提醒</el-button>
      <el-tag type="info" style="margin-left: 12px">已过期 / 30天内到期将自动高亮</el-tag>
    </div>

    <!-- 到期汇总：登录进来第一眼就能看到几台过期 / 即将到期 -->
    <expiry-summary
      :expired="stats.expired"
      :soon="stats.soon"
      :total="stats.total"
      unit="台"
    />

    <!-- 表格 -->
    <el-table ref="mainTable" class="pdf-table" :data="tableData" row-key="id" border v-loading="loading" :row-class-name="rowClassName">
      <el-table-column type="selection" width="55" />
      <el-table-column type="index" label="序号" width="60" />
      <el-table-column prop="device_name" label="设备名称" min-width="140" />
      <el-table-column prop="department" label="科室" width="110" />
      <el-table-column prop="report_no" label="报告编号" width="140" />
      <el-table-column prop="report_date" label="报告日期" width="120" />
      <el-table-column prop="verify_date" label="鉴定日期" width="120" />
      <el-table-column prop="valid_period" label="有效期(月)" width="100" align="center" />
      <el-table-column prop="expire_date" label="有效期至" width="120" />
      <el-table-column label="距到期天数" width="110" align="center">
        <template slot-scope="scope">
          <span v-if="scope.row.days_until_expire == null">-</span>
          <el-tag v-else :type="tagType(scope.row.days_until_expire)" size="medium">
            {{ scope.row.days_until_expire }} 天
          </el-tag>
        </template>
      </el-table-column>
      <el-table-column label="PDF" width="130">
        <template slot-scope="scope">
          <el-link v-if="scope.row.pdf_path_1" type="primary" :href="`/media/${scope.row.pdf_path_1}`" target="_blank" icon="el-icon-document">PDF1</el-link>
          <el-link v-if="scope.row.pdf_path_2" type="primary" :href="`/media/${scope.row.pdf_path_2}`" target="_blank" icon="el-icon-document" style="margin-left: 6px">PDF2</el-link>
        </template>
      </el-table-column>
      <el-table-column prop="remark" label="备注" min-width="120" show-overflow-tooltip />
      <el-table-column label="操作" width="140" fixed="right">
        <template slot-scope="scope">
          <el-link type="primary" @click="handleEdit(scope.row)">编辑</el-link>
          <el-link type="danger" style="margin-left: 10px" @click="handleDelete(scope.row)">删除</el-link>
        </template>
      </el-table-column>
    </el-table>

    <!-- 分页 -->
    <el-pagination
      class="pagination"
      background
      layout="total, sizes, prev, pager, next, jumper"
      :total="total"
      :page-size="pageSize"
      :current-page="page"
      :page-sizes="[10, 20, 50]"
      @size-change="handleSizeChange"
      @current-change="handlePageChange"
    />

    <!-- 新增/编辑弹窗 -->
    <PdfReminderForm
      :visible.sync="formVisible"
      :form-data="currentRow"
      @success="handleSuccess"
    />
  </div>
</template>

<script>
import pdfReminderApi from '../api/pdf_reminder'
import PdfReminderForm from './PdfReminderForm.vue'
import ExpirySummary from './ExpirySummary.vue'
import DepartmentSelect from './DepartmentSelect.vue'

export default {
  name: 'PdfReminderList',
  components: { PdfReminderForm, ExpirySummary, DepartmentSelect },
  data() {
    return {
      queryForm: {
        device_name: '',
        department: '',
        report_date_start: '',
        report_date_end: '',
      },
      tableData: [],
      total: 0,
      // 顶部汇总：已过期 / 30 天内到期 / 合计（跟随当前筛选，后端 stats 接口给）
      stats: { expired: 0, soon: 0, total: 0 },
      page: 1,
      pageSize: 10,
      loading: false,
      formVisible: false,
      currentRow: null,
      // 保存后要高亮定位的那一行（id）
      highlightId: null,
      _highlightTimer: null,
    }
  },
  mounted() {
    this.handleSearch()
  },
  methods: {
    async handleSearch() {
      this.loading = true
      try {
        const res = await pdfReminderApi.list({
          ...this.queryForm,
          page: this.page,
          page_size: this.pageSize,
        })
        this.tableData = res.data.results
        this.total = res.data.count
      } finally {
        this.loading = false
      }
      // 汇总与列表同源筛选，列表刷新后顺手刷新（失败不影响列表）
      this.fetchStats()
    },
    // 顶部汇总：不带分页参数，只要筛选条件
    async fetchStats() {
      try {
        const res = await pdfReminderApi.stats({ ...this.queryForm })
        const d = res.data || {}
        this.stats = {
          expired: d.expired || 0,
          soon: d.soon || 0,
          total: d.total || 0,
        }
      } catch (e) {
        // 汇总失败静默保持上次值，不打扰列表使用
      }
    },
    handleReset() {
      this.queryForm = { device_name: '', department: '', report_date_start: '', report_date_end: '' }
      this.page = 1
      this.handleSearch()
    },
    handleSizeChange(val) {
      this.pageSize = val
      this.page = 1
      this.handleSearch()
    },
    handlePageChange(val) {
      this.page = val
      this.handleSearch()
    },
    handleAdd() {
      this.currentRow = null
      this.formVisible = true
    },
    handleEdit(row) {
      this.currentRow = row
      this.formVisible = true
    },
    handleDelete(row) {
      this.$confirm(`确定删除「${row.device_name}」的提醒记录吗？`, '提示', {
        confirmButtonText: '确定',
        cancelButtonText: '取消',
        type: 'warning',
      }).then(async () => {
        await pdfReminderApi.delete(row.id)
        this.$message.success('删除成功')
        this.handleSearch()
      }).catch(() => {})
    },
    tagType(days) {
      if (days < 0) return 'danger'
      if (days <= 30) return 'warning'
      return 'success'
    },
    rowClassName({ row }) {
      const cls = []
      const d = row.days_until_expire
      if (d != null && d < 0) cls.push('expired-row')
      else if (d != null && d <= 30) cls.push('soon-row')
      // 刚保存的那条额外闪一下（叠加在到期色之上，2.6s 后自动消失）
      if (this.highlightId && row.id === this.highlightId) cls.push('row-highlight')
      return cls.join(' ')
    },
    // 表单保存成功回调：新增一律回到默认视图（清筛选+回第1页），再滚动定位到该条。
    // 否则带着筛选刷新时新记录可能不在当前页，用户会以为「没保存成功」。
    async handleSuccess(payload) {
      const info = payload || {}
      if (info.isEdit) {
        await this.handleSearch()
      } else {
        this.queryForm = {
          device_name: '',
          department: '',
          report_date_start: '',
          report_date_end: '',
        }
        this.page = 1
        await this.handleSearch()
      }
      this.focusSavedRow(info.id)
    },
    // 把指定记录滚到可视区中间并短暂高亮
    async focusSavedRow(id) {
      if (!id) return
      this.highlightId = id
      if (this._highlightTimer) clearTimeout(this._highlightTimer)
      this._highlightTimer = setTimeout(() => {
        this.highlightId = null
      }, 2600)
      await this.$nextTick()
      const idx = (this.tableData || []).findIndex(r => r.id === id)
      if (idx < 0) return // 当前页没有（被筛选挡掉），不强行滚动
      const tableEl = this.$refs.mainTable && this.$refs.mainTable.$el
      if (!tableEl) return
      const trs = tableEl.querySelectorAll('.el-table__body-wrapper tbody > tr')
      const tr = trs[idx]
      if (tr && tr.scrollIntoView) tr.scrollIntoView({ block: 'center', behavior: 'smooth' })
    },
  },
}
</script>

<style scoped>
.search-card {
  margin-bottom: 16px;
}
.search-card /deep/ .el-card__body {
  padding: 18px 20px;
}
/* 查询区缩小一号：label 13px（控件尺寸由 size="small" 接管） */
.search-form /deep/ .el-form-item__label {
  font-size: 13px;
}
.search-form /deep/ .el-input__inner {
  border-color: #409eff;
}
.range-separator {
  margin: 0 8px;
  color: #909399;
}
.toolbar {
  margin-bottom: 14px;
}
.pagination {
  margin-top: 16px;
  text-align: right;
}
/* 到期高亮：已过期=红、即将到期=橙
   ⚠️ 配色必须在「暖米白」底（App.vue 全局 #f6f1e7）上真的看得出来。
   原来写的 #fef0f0 / #fdf6ec 是给白底设计的，放到米白底上几乎是同一个颜色：
     #f6f1e7 米白  vs  #fdf6ec 淡橙 → RGB 只差 (7,5,5)，肉眼看不到
     #f6f1e7 米白  vs  #fef0f0 淡红 → 只剩一点点粉
   这就是"改了暖色调之后到期行不变色"的真正原因（不是 CSS 没生效，是颜色撞底）。
   现在换成明显偏红/偏橙的两档，并加左侧色条，扫一眼就能分出轻重。 */
.pdf-table /deep/ .el-table__body tr.expired-row > td {
  background-color: #fad7d5 !important;
}
.pdf-table /deep/ .el-table__body tr.expired-row > td:first-child {
  border-left: 3px solid #f56c6c;
}
.pdf-table /deep/ .el-table__body tr.soon-row > td {
  background-color: #fbe9c9 !important;
}
.pdf-table /deep/ .el-table__body tr.soon-row > td:first-child {
  border-left: 3px solid #e6a23c;
}
/* hover 用同色系加深，不要跳成蓝色 —— 否则一悬停就分不出这行是过期的 */
.pdf-table /deep/ .el-table__body tr.expired-row:hover > td {
  background-color: #f6c6c3 !important;
}
.pdf-table /deep/ .el-table__body tr.soon-row:hover > td {
  background-color: #f7dfb2 !important;
}
.pdf-table /deep/ .el-table__body tr:hover > td {
  background-color: #ecf5ff !important;
}
/* 新增/编辑保存后定位高亮：用 inset box-shadow 叠一层半透明绿，
   不直接改 background-color，避免把「到期红/橙」状态色冲掉；类移除时自带淡出。 */
.pdf-table /deep/ .el-table__body tr.row-highlight > td {
  box-shadow: inset 0 0 0 9999px rgba(103, 194, 58, 0.22);
  transition: box-shadow 0.4s ease;
}
</style>
